import { useCallback, useEffect, useState } from 'react'
import { usePageHeaderActions } from '@context/PageHeaderContext'
import { useAuth } from '@context/AuthContext'
import { Plus, RefreshCcw, Users, AlertTriangle } from 'lucide-react'
import UserFilters from './UserFilters'
import UserTable from './UserTable'
import UserFormDrawer from './UserFormDrawer'
import UserCreateButton from './UserCreateButton'
import UserDetailsDrawer from './UserDetailsDrawer'
import RolesTab from './RolesTab'
import AccessRequestsTab from './AccessRequestsTab'
import PortalClientRequestsTab from './PortalClientRequestsTab'
import PortalTemporaryPasswordModal from './PortalTemporaryPasswordModal'
import { ConfirmModal } from '@components/shared/Modal'
import { useNotification } from '@context/NotificationContext'
import { generatePortalTemporaryPasswordForUser } from '@services/portalService'
import {
  createUser,
  deleteUser,
  getUserDetails,
  listRoles,
  listUsers,
  paginateUsers,
  resetUserPassword,
  toggleUserStatus,
  updateUser,
} from '@services/userManagementService'

const DEFAULT_FILTERS = {
  search: '', status: 'ALL', profile: 'ALL', sortBy: 'lastAccessAt', page: 1, pageSize: 5,
}

const isPortalClient = (user) => String(user?.profile || '').toUpperCase() === 'CLIENT'

export default function UsersPage() {
  const { canListUsers, canCreateUsers, canEditUsers, isAdmin, isSuperAdmin } = useAuth()
  const canManagePortal = isAdmin || isSuperAdmin
  const { showError, showSuccess, showInfo } = useNotification()
  const setPageHeaderActions = usePageHeaderActions()
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [searchValue, setSearchValue] = useState('')
  const [allUsers, setAllUsers] = useState([])
  const [users, setUsers] = useState([])
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, pageSize: 5 })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [saving, setSaving] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [formState, setFormState] = useState({ open: false, mode: 'create', user: null })
  const [selectedUser, setSelectedUser] = useState(null)
  const [confirmAction, setConfirmAction] = useState(null)
  const [temporaryCredential, setTemporaryCredential] = useState(null)
  const [activeTab, setActiveTab] = useState('users')
  const [roles, setRoles] = useState([])

  useEffect(() => {
    setPageHeaderActions(activeTab === 'users' && canCreateUsers ? (
      <UserCreateButton className="btn btn-primary users-primary-action" onClick={() => setFormState({ open: true, mode: 'create', user: null })}>
        <Plus size={18} /> Novo usuário
      </UserCreateButton>
    ) : null)
    return () => setPageHeaderActions(null)
  }, [activeTab, canCreateUsers, setPageHeaderActions])

  useEffect(() => {
    const timer = setTimeout(() => setFilters((prev) => ({ ...prev, search: searchValue, page: 1 })), 350)
    return () => clearTimeout(timer)
  }, [searchValue])

  const loadUsers = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const response = await listUsers()
      setAllUsers(response)
    } catch (error) {
      setLoadError(error.message)
      setAllUsers([])
      setUsers([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (canListUsers) loadUsers()
    else { setLoading(false); setAllUsers([]) }
  }, [canListUsers, loadUsers])

  useEffect(() => {
    if (canListUsers) listRoles().then(setRoles).catch(() => setRoles([]))
  }, [canListUsers])

  useEffect(() => {
    const response = paginateUsers(allUsers, filters)
    setUsers(response.data)
    setPagination((prev) => ({ ...prev, page: response.page, totalPages: response.totalPages, total: response.total, pageSize: filters.pageSize }))
  }, [allUsers, filters])

  const handleFilterChange = (field, value) => setFilters((prev) => ({ ...prev, [field]: value, page: 1 }))

  const handleFormSubmit = async (formData) => {
    if (formState.mode === 'create' && !canCreateUsers) {
      showError('Somente Admin e Super Admin podem criar usuários.')
      return
    }

    setSaving(true)
    setSaveError('')
    try {
      if (formState.mode === 'create') {
        await createUser(formData)
        showSuccess('Usuário criado com sucesso.')
      } else {
        await updateUser(formState.user.id, formData)
        showSuccess('Usuário atualizado com sucesso.')
      }
      setFormState({ open: false, mode: 'create', user: null })
      await loadUsers()
    } catch (error) {
      setSaveError(error.message)
      showError(error.message)
    } finally {
      setSaving(false)
    }
  }

  const openConfirmation = (type, user) => setConfirmAction({ type, user })
  const handleConfirm = async () => {
    if (!confirmAction) return
    try {
      if (confirmAction.type === 'toggleStatus') { await toggleUserStatus(confirmAction.user); showSuccess('Status do usuário atualizado.') }
      if (confirmAction.type === 'resetPassword') {
        if (isPortalClient(confirmAction.user)) {
          const result = await generatePortalTemporaryPasswordForUser(confirmAction.user.id)
          if (!result.success || !result.data?.senhaTemporaria) throw new Error(result.error || 'Não foi possível gerar a senha temporária.')
          setTemporaryCredential(result.data)
          showSuccess('Nova senha temporária gerada para o cliente.')
        } else {
          const result = await resetUserPassword(confirmAction.user.id)
          showInfo(result.message)
        }
      }
      if (confirmAction.type === 'delete') { await deleteUser(confirmAction.user.id); showSuccess('Usuário removido com sucesso.') }
      await loadUsers()
    } catch (error) { showError(error.message) }
  }

  const handleViewUser = async (user) => {
    setDetailsLoading(true)
    try { setSelectedUser(await getUserDetails(user.id)) } catch (error) { showError(error.message) } finally { setDetailsLoading(false) }
  }
  const handleTableAction = (action, user) => {
    if (action === 'view') return handleViewUser(user)
    if (action === 'edit') setFormState({ open: true, mode: 'edit', user })
    if (action === 'toggleStatus' || action === 'resetPassword' || action === 'delete') openConfirmation(action, user)
  }

  const resetIsPortalClient = confirmAction?.type === 'resetPassword' && isPortalClient(confirmAction?.user)

  const renderUsersTab = () => (
    <>
      <div className="users-page-top">
        <UserFilters filters={filters} searchValue={searchValue} onSearchChange={setSearchValue} onFilterChange={handleFilterChange} />
        {loadError ? <div className="users-feedback users-feedback-error"><AlertTriangle size={18} /><div><strong>Erro ao carregar usuários</strong><p>{loadError}</p></div><button type="button" className="btn btn-outline" onClick={loadUsers}><RefreshCcw size={16} /> Tentar novamente</button></div> : null}
        {!loading && !loadError && users.length === 0 ? <div className="users-feedback users-feedback-empty"><Users size={20} /><div><strong>Nenhum usuário encontrado</strong><p>A API de usuários não retornou registros para os filtros informados.</p></div>{canCreateUsers ? <UserCreateButton className="btn btn-primary users-inline-create" onClick={() => setFormState({ open: true, mode: 'create', user: null })}>Cadastrar usuário</UserCreateButton> : null}</div> : null}
      </div>
      {!loadError && (loading || users.length > 0) ? <UserTable users={users} loading={loading} pagination={pagination} onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))} onPageSizeChange={(pageSize) => setFilters((prev) => ({ ...prev, pageSize, page: 1 }))} onAction={handleTableAction} canEdit={canEditUsers} /> : null}
      {(formState.mode === 'create' ? canCreateUsers : canEditUsers) ? <UserFormDrawer isOpen={formState.open} mode={formState.mode} user={formState.user} saving={saving} saveError={saveError} roles={roles} onClose={() => setFormState({ open: false, mode: 'create', user: null })} onSubmit={handleFormSubmit} /> : null}
      <UserDetailsDrawer user={selectedUser} isOpen={Boolean(selectedUser) || detailsLoading} loading={detailsLoading} onClose={() => setSelectedUser(null)} />
      <ConfirmModal
        isOpen={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        title={confirmAction?.type === 'delete' ? 'Excluir usuário' : confirmAction?.type === 'resetPassword' ? (resetIsPortalClient ? 'Gerar senha temporária' : 'Resetar senha') : 'Alterar status do usuário'}
        message={confirmAction?.type === 'delete'
          ? `Deseja realmente excluir ${confirmAction?.user?.fullName}? Esta ação não poderá ser desfeita.`
          : confirmAction?.type === 'resetPassword'
            ? resetIsPortalClient
              ? `Gerar uma nova senha temporária para ${confirmAction?.user?.fullName}? A senha atual deixará de funcionar e os refresh tokens ativos serão revogados.`
              : `Confirma o reset de senha para ${confirmAction?.user?.fullName}?`
            : `Deseja ${confirmAction?.user?.status === 'ACTIVE' ? 'inativar' : 'ativar'} ${confirmAction?.user?.fullName}?`}
        confirmText={confirmAction?.type === 'delete' ? 'Excluir' : resetIsPortalClient ? 'Gerar senha' : 'Confirmar'}
      />
      <PortalTemporaryPasswordModal credential={temporaryCredential} onClose={() => setTemporaryCredential(null)} />
    </>
  )

  return (
    <div className="users-page">
      <div className="users-tabs" role="tablist">
        <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>Usuários</button>
        {canEditUsers ? <button className={activeTab === 'roles' ? 'active' : ''} onClick={() => setActiveTab('roles')}>Perfis e permissões</button> : null}
        {canManagePortal ? <button className={activeTab === 'portal-access' ? 'active' : ''} onClick={() => setActiveTab('portal-access')}>Solicitações de acesso</button> : null}
        {canManagePortal ? <button className={activeTab === 'portal-workspace' ? 'active' : ''} onClick={() => setActiveTab('portal-workspace')}>Central do cliente</button> : null}
      </div>
      {activeTab === 'roles' && canEditUsers
        ? <RolesTab onRolesChange={setRoles} />
        : activeTab === 'portal-access' && canManagePortal
          ? <AccessRequestsTab />
          : activeTab === 'portal-workspace' && canManagePortal
            ? <PortalClientRequestsTab />
            : renderUsersTab()}
    </div>
  )
}
